import React, { useRef, useState } from "react";
import DOMPurify from "dompurify";
import { Upload, FileCheck, Loader2, Download } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { uploadFormFile } from "../lib/uploadFormFile";
import api from "../api";

// Renders a single dynamic form field. Display-only types ignore value/onChange.
export default function FormField({ field, value, onChange, fileMeta, onFile, readOnly, formId }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const t = field.type;
  const req = field.required;

  if (t === "section") return <h3 className="mt-2 border-b border-slate-100 pb-2 text-sm font-bold uppercase tracking-wide text-slate-700" data-testid={`field-${field.id}`}>{field.label}</h3>;
  if (t === "divider") return <hr className="my-2 border-slate-100" />;
  if (t === "instructions") return <p className="text-sm text-slate-500" data-testid={`field-${field.id}`}>{field.content || field.label}</p>;
  if (t === "richtext") return <div className="lesson-content text-sm" data-testid={`field-${field.id}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(field.content || "") }} />;

  const label = (
    <Label className="text-sm text-slate-700">{field.label} {req && <span className="text-rose-500">*</span>}</Label>
  );

  const wrap = (input) => (
    <div className="space-y-1.5" data-testid={`field-${field.id}`}>{label}{input}</div>
  );

  if (readOnly) {
    let display = value;
    if (Array.isArray(value)) display = value.join(", ");
    if (t === "file") display = fileMeta?.name || "—";
    if (t === "signature") return wrap(<div className="font-signature text-2xl text-slate-800">{value || "—"}</div>);
    if (t === "yesno") display = value === true || value === "yes" ? "Yes" : value === false || value === "no" ? "No" : "—";
    return wrap(<div className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-sm text-slate-700">{display || "—"}</div>);
  }

  switch (t) {
    case "paragraph":
    case "address":
      return wrap(<Textarea rows={t === "address" ? 2 : 4} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`input-${field.id}`} placeholder={field.placeholder} />);
    case "email":
      return wrap(<Input type="email" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`input-${field.id}`} placeholder={field.placeholder} />);
    case "phone":
      return wrap(<Input type="tel" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`input-${field.id}`} placeholder={field.placeholder} />);
    case "date":
      return wrap(<Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`input-${field.id}`} />);
    case "number":
      return wrap(<Input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`input-${field.id}`} placeholder={field.placeholder} />);
    case "currency":
      return wrap(<div className="relative"><span className="absolute left-3 top-2.5 text-slate-400">$</span><Input type="number" step="0.01" className="pl-6" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`input-${field.id}`} /></div>);
    case "dropdown":
      return wrap(
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger data-testid={`input-${field.id}`}><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>{(field.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      );
    case "radio":
      return wrap(
        <div className="space-y-1.5">{(field.options || []).map((o) => (
          <label key={o} className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
            <input type="radio" name={field.id} checked={value === o} onChange={() => onChange(o)} className="accent-violet-600" data-testid={`input-${field.id}-${o}`} />{o}
          </label>))}
        </div>
      );
    case "yesno":
      return wrap(
        <div className="flex gap-4">{["Yes", "No"].map((o) => (
          <label key={o} className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
            <input type="radio" name={field.id} checked={value === o.toLowerCase()} onChange={() => onChange(o.toLowerCase())} className="accent-violet-600" data-testid={`input-${field.id}-${o}`} />{o}
          </label>))}
        </div>
      );
    case "checkboxes": {
      const arr = Array.isArray(value) ? value : [];
      return wrap(
        <div className="space-y-1.5">{(field.options || []).map((o) => (
          <label key={o} className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={arr.includes(o)} onChange={() => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o])} className="accent-violet-600" data-testid={`input-${field.id}-${o}`} />{o}
          </label>))}
        </div>
      );
    }
    case "signature":
      return wrap(<>
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`input-${field.id}`} placeholder="Type your full legal name" />
        {value && <div className="mt-1 rounded-md border border-slate-100 bg-slate-50 px-3 py-2 font-signature text-2xl text-slate-800">{value}</div>}
      </>);
    case "initials":
      return wrap(<Input value={value || ""} maxLength={5} className="w-24" onChange={(e) => onChange(e.target.value.toUpperCase())} data-testid={`input-${field.id}`} placeholder="e.g. JS" />);
    case "file":
      return wrap(
        <div>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.png,.jpg,.jpeg" className="hidden" data-testid={`input-${field.id}`}
            onChange={async (e) => {
              const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
              setBusy(true);
              try { const meta = await uploadFormFile(formId, field.id, f); onFile(meta); }
              catch (err) { alert(err.message || "Upload failed"); }
              finally { setBusy(false); }
            }} />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : fileMeta ? <FileCheck className="h-4 w-4 text-emerald-500" /> : <Upload className="h-4 w-4" />}
            {fileMeta ? `Replace: ${fileMeta.name}` : "Upload file (PDF, DOCX, PNG, JPG)"}
          </button>
        </div>
      );
    default:
      return wrap(<Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`input-${field.id}`} placeholder={field.placeholder} />);
  }
}
